import type { Dayjs } from 'dayjs'

/**
 * 获取 date 所在周的起始日
 * @param date - dayjs 日期对象
 * @param weekStart - 一周的第一天 (0=周日, 1=周一, ..., 6=周六)
 */
export const getWeekStartDay = (date: Dayjs, weekStart: number): Dayjs => {
  const diff = (date.day() - weekStart + 7) % 7
  return date.subtract(diff, 'day').startOf('day')
}

/**
 * 获取 date 所在周的结束日
 * @param date - dayjs 日期对象
 * @param weekStart - 一周的第一天 (0=周日, 1=周一, ..., 6=周六)
 */
export const getWeekEndDay = (date: Dayjs, weekStart: number): Dayjs => {
  return getWeekStartDay(date, weekStart).add(6, 'day').endOf('day')
}

/**
 * 判断两个日期是否在同一周
 * @param d1 - dayjs 日期对象
 * @param d2 - dayjs 日期对象
 * @param weekStart - 一周的第一天 (0=周日, 1=周一, ..., 6=周六)
 */
export const isSameWeek = (
  d1: Dayjs,
  d2: Dayjs,
  weekStart: number
): boolean => {
  return getWeekStartDay(d1, weekStart).isSame(
    getWeekStartDay(d2, weekStart),
    'day'
  )
}

/**
 * 获取 date 所在的周号（基于自定义 weekStart）
 * 周号计算规则：包含1月4日的那一周为第1周（ISO 8601 风格，但适配自定义 weekStart）
 * @param date - dayjs 日期对象
 * @param weekStart - 一周的第一天 (0=周日, 1=周一, ..., 6=周六)
 */
export const getWeekNumber = (date: Dayjs, weekStart: number): number => {
  // 找到该日期所在周的周四（ISO week 的标识日）
  // 对于自定义 weekStart，"周四"等价于 weekStart + 3 (mod 7)
  const ws = getWeekStartDay(date, weekStart)
  const thursday = ws.add(3, 'day')
  // 该年1月1日
  const jan1 = thursday.startOf('year')
  // 1月1日所在周的周四
  const jan1WeekStart = getWeekStartDay(jan1, weekStart)
  const jan1Thursday = jan1WeekStart.add(3, 'day')
  // 计算周数差
  const diff = thursday.diff(jan1Thursday, 'day')
  return Math.floor(diff / 7) + 1
}

/**
 * 获取 date 的 weekYear（基于自定义 weekStart）
 * weekYear 可能与 date.year() 不同（年末/年初边界情况）
 * @param date - dayjs 日期对象
 * @param weekStart - 一周的第一天 (0=周日, 1=周一, ..., 6=周六)
 */
export const getWeekYear = (date: Dayjs, weekStart: number): number => {
  const ws = getWeekStartDay(date, weekStart)
  const thursday = ws.add(3, 'day')
  return thursday.year()
}
